// ============================================================================
// Users & Roles — page shell
// ----------------------------------------------------------------------------
// Reached from the top-right user dropdown (route: /users-roles). Renders the
// Keycloak banner, the segmented tab bar, and the active tab. Loads real member
// + pending data from the BFF (/api/users) — see src/scenes/usersRoles/api.js
// and docs/users-roles-api.md. Mutations are still optimistic (Phase 2/3 wires
// the write paths).
// ============================================================================
import { useState, useEffect, useCallback } from "react";
import {
  Box, Snackbar, Alert, Button, LinearProgress,
  Select, MenuItem, Typography,
} from "@mui/material";
import Header from "../../components/Header";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import { useAuth } from "../../contexts/AuthContext";

import KeycloakBanner from "./components/KeycloakBanner";
import UsersTabsBar from "./components/UsersTabsBar";
import MembersTab from "./components/MembersTab";
import InviteTab from "./components/InviteTab";
import RolesTab from "./components/RolesTab";
import ActivityTab from "./components/ActivityTab";
import IntegrationFlowsTab from "./components/IntegrationFlowsTab";
import OrganizationsTab from "./components/OrganizationsTab";

import { listMembers, listPending, setActiveOrg, getOrganizations } from "./api";
import { getKeycloakAdminUrl } from "../../config/keycloak";

const BASE_TABS = [
  { key: "members", label: "Members" },
  { key: "invite", label: "Invite" },
  { key: "roles", label: "Roles" },
  { key: "activity", label: "Activity Log" },
  { key: "flows", label: "Integration Flows" },
];

const UsersRoles = () => {
  const { user, hasRole } = useAuth();
  const isSuperadmin = hasRole?.("platform_superadmin");
  const [tab, setTab] = useState("members");
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });

  // Superadmin org switcher — scope all org-aware reads to the selected org.
  const [orgList, setOrgList] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("");

  const tabs = isSuperadmin
    ? [...BASE_TABS.slice(0, 4), { key: "organizations", label: "Organizations" }, BASE_TABS[4]]
    : BASE_TABS;

  const showToast = (msg, severity = "success") => setToast({ open: true, msg, severity });
  const closeToast = () => setToast((t) => ({ ...t, open: false }));

  // Business name shown in the header: org attribute from the token if present.
  const orgName =
    user?.organization ||
    user?.tokenParsed?.organization ||
    (Array.isArray(user?.tokenParsed?.organization)
      ? user.tokenParsed.organization[0]
      : null);

  // BFF call: GET /api/users + GET /api/users?enabled=false  (§2.1, §3.4)
  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [members, pend] = await Promise.all([listMembers(), listPending()]);
      setUsers(members || []);
      setPending(pend || []);
    } catch (err) {
      console.error("[UsersRoles] failed to load members:", err);
      setLoadError(
        "Could not load members from the server. Confirm you have an admin role and that the API + Keycloak are running."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Superadmin: load the org list for the switcher.
  useEffect(() => {
    if (isSuperadmin) getOrganizations().then(setOrgList).catch(() => setOrgList([]));
  }, [isSuperadmin]);

  const handleOrgChange = (value) => {
    setSelectedOrg(value);
    setActiveOrg(value || null);
    load();
  };

  // Deep-link helper used by the "+ Invite User" header action
  const goInvite = () => setTab("invite");
  // Forward the superadmin to the Keycloak admin login page for the active
  // environment (localhost in local/dev, the designated Keycloak URL in
  // dev/qa/prod). Opens in a new tab; Keycloak itself enforces admin auth.
  const handleOpenAdmin = () => {
    if (!isSuperadmin) {
      showToast("Only platform superadmins can open the Keycloak admin console.", "warning");
      return;
    }
    const adminUrl = getKeycloakAdminUrl();
    showToast("Opening Keycloak admin console…", "info");
    window.open(adminUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1440, mx: "auto" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Header
          icon={<ManageAccountsOutlinedIcon />}
          title="Users & Roles"
          subtitle={
            `Manage platform members, roles, and access` +
            (orgName ? ` · ${orgName}` : "") +
            ` · ${users.length} member${users.length === 1 ? "" : "s"}`
          }
        />
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {isSuperadmin && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Typography sx={{ fontSize: "0.6875rem", color: "var(--fx-text-secondary)" }}>
                Org
              </Typography>
              <Select
                size="small"
                value={selectedOrg}
                displayEmpty
                onChange={(e) => handleOrgChange(e.target.value)}
                sx={{ minWidth: 160, fontSize: "0.75rem" }}
              >
                <MenuItem value="" sx={{ fontSize: "0.75rem" }}>All organizations</MenuItem>
                {orgList.map((o) => (
                  <MenuItem key={o.id} value={o.name} sx={{ fontSize: "0.75rem" }}>
                    {o.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}
          <Button variant="outlined" size="small" onClick={() => setTab("activity")}>
            Audit Log
          </Button>
          <Button variant="contained" size="small" onClick={goInvite}>
            + Invite User
          </Button>
        </Box>
      </Box>

      <KeycloakBanner onOpenAdmin={handleOpenAdmin} />

      <UsersTabsBar active={tab} onChange={setTab} tabs={tabs} />

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {loadError && !loading && (
        <Alert
          severity="error"
          sx={{ mb: 2, fontSize: "0.8125rem" }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}

      {tab === "members" && (
        <MembersTab users={users} setUsers={setUsers} showToast={showToast} />
      )}
      {tab === "invite" && (
        <InviteTab
          pending={pending}
          setPending={setPending}
          setUsers={setUsers}
          showToast={showToast}
          reload={load}
          onDone={() => setTab("members")}
        />
      )}
      {tab === "roles" && <RolesTab users={users} showToast={showToast} />}
      {tab === "activity" && <ActivityTab showToast={showToast} />}
      {tab === "organizations" && isSuperadmin && <OrganizationsTab showToast={showToast} />}
      {tab === "flows" && <IntegrationFlowsTab />}

      <Snackbar
        open={toast.open}
        autoHideDuration={3200}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={closeToast}
          severity={toast.severity}
          variant="filled"
          sx={{ fontSize: "0.8125rem", alignItems: "center" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersRoles;
